import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  useGetShopCertificateListDataQuery,
  useUpdateShopCertificateTableHeadDataMutation,
} from "../../../store/queries/Master";
import { updateConfig as globalUpdateConfig } from "../../../store/slices/Global";
import { updateConfig } from "../../../store/slices/ShopCertificate/shopCertificateSlice";
import { bulkDownloadCertificateUrls } from "../api";

const useShopCertificate = () => {
  const paginationOptions = [
    { label: "10 Per page", value: 10 },
    { label: "20 Per page", value: 20 },
    { label: "40 Per page", value: 40 },
  ];

  const dispatch = useDispatch();

  const [isBulkDownloading, setIsBulkDownloading] = useState(false);

  const { tableFields, showEditModal } = useSelector((state) => state.global);
  const shopCertificateState = useSelector((state) => state.shopCertificate);

  const {
    data: shopCertificateList = {},
    isLoading,
    isFetching,
    refetch,
  } = useGetShopCertificateListDataQuery({
    page_size: shopCertificateState.currentPageSize,
    page: shopCertificateState.currentPage,
    sort_by: shopCertificateState.sortBy,
    sort_order: shopCertificateState.sortOrder,
    search: shopCertificateState.search,
  });

  const [updateShopCertificateFields] =
    useUpdateShopCertificateTableHeadDataMutation();

  useEffect(() => {
    dispatch(
      globalUpdateConfig((state) => {
        state.tableFields = shopCertificateList?.data?.fields;
      }),
    );
    // eslint-disable-next-line
  }, [shopCertificateList]);

  useEffect(() => {
    if (shopCertificateState.clearSelection) {
      dispatch(
        updateConfig((state) => {
          state.clearSelection = false;
        }),
      );
    }
    // eslint-disable-next-line
  }, [shopCertificateState.clearSelection]);

  const tableFieldsWithAction = useMemo(() => {
    return {
      ...tableFields,
      action: { label: "Action", key: "action" },
    };
  }, [tableFields]);

  const handleEditColumnsClick = () => {
    dispatch(
      globalUpdateConfig((state) => {
        state.showEditModal = !state.showEditModal;
      }),
    );
  };

  const handleSearch = (e) => {
    dispatch(
      updateConfig((state) => {
        state.search = e.target.value;
        state.currentPage = 1;
      }),
    );
  };

  const handleClearClick = () => {
    dispatch(
      updateConfig((state) => {
        state.search = "";
      }),
    );
  };

  const handlePageSize = ({ value: page_size }) => {
    dispatch(
      updateConfig((state) => {
        state.currentPageSize = page_size;
        state.currentPage = 1;
      }),
    );
  };

  const handlePagination = (page) => {
    dispatch(
      updateConfig((state) => {
        state.currentPage = page;
      }),
    );
  };

  const handleBulkUploadClick = () => {
    dispatch(
      updateConfig((state) => {
        state.showBulkUpload = true;
      }),
    );
  };

  const handleBulkUploadClose = () => {
    dispatch(
      updateConfig((state) => {
        state.showBulkUpload = false;
      }),
    );
  };

  const handleSort = (label) => {
    if (shopCertificateState.sortBy === label) {
      dispatch(
        updateConfig((state) => {
          state.currentPage = 1;
          state.sortOrder =
            shopCertificateState.sortOrder === "asc" ? "desc" : "asc";
        }),
      );
    } else {
      dispatch(
        updateConfig((state) => {
          state.currentPage = 1;
          state.sortBy = label;
          state.sortOrder = "asc";
        }),
      );
    }
  };

  const triggerUrlDownload = async (url, filename) => {
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error("download request failed");
      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const downloadLink = document.createElement("a");
      downloadLink.href = objectUrl;
      downloadLink.setAttribute("download", filename || "");
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      window.URL.revokeObjectURL(objectUrl);
    } catch (error) {
      // Fallback when the file host blocks cross-origin fetch (CORS):
      // a hidden iframe triggers the download without the popup blocker.
      const iframe = document.createElement("iframe");
      iframe.style.display = "none";
      iframe.src = url;
      document.body.appendChild(iframe);
      setTimeout(() => {
        if (iframe.parentNode) document.body.removeChild(iframe);
      }, 60000);
    }
  };

  const handleDownload = (data) => {
    if (!data?.download_url) return;
    triggerUrlDownload(data.download_url);
  };

  const handleBulkDownload = (checked) => {
    const ids = checked || [];
    if (!ids.length) {
      toast.warning("Please select at least one certificate");
      return;
    }
    if (isBulkDownloading) return;
    setIsBulkDownloading(true);
    bulkDownloadCertificateUrls({ ids })
      .then(async (response) => {
        if (response?.status === 200 && response?.data?.status) {
          const items = Array.isArray(response?.data?.data)
            ? response.data.data
            : [];
          const downloadable = items.filter(
            (item) => item?.can_download && item?.download_url,
          );
          if (!downloadable.length) {
            toast.error("No certificates available to download");
          } else {
            for (const item of downloadable) {
              await triggerUrlDownload(
                item.download_url,
                item?.name ? `${item.name}.zip` : "",
              );
            }
            if (downloadable.length < items.length) {
              toast.warn(
                `${items.length - downloadable.length} certificate(s) not available to download`,
              );
            }
            toast.success("Downloaded successfully");
            dispatch(
              updateConfig((state) => {
                state.clearSelection = true;
              }),
            );
          }
        } else {
          toast.error(response?.data?.msg || "Something went wrong");
        }
        setIsBulkDownloading(false);
      })
      .catch(() => {
        toast.error("Something went wrong");
        setIsBulkDownloading(false);
      });
  };

  const getRow = (field, data) => {
    const rows = {
      common: (field, data) => <p className="pro-mb-0">{data[field]}</p>,
      action: (field, data) =>
        data?.can_download && data?.download_url ? (
          <button
            className="pro-btn pro-btn-primary pro-btn-sm pro-items-center"
            onClick={() => handleDownload(data)}
            title="Download"
          >
            <span className="material-symbols-outlined">download</span>
          </button>
        ) : (
          <p className="pro-mb-0">--</p>
        ),
    };
    return rows?.[field]?.(field, data) ?? rows["common"](field, data);
  };

  return {
    shopCertificateState,
    shopCertificateList,
    isLoading,
    isFetching,
    refetch,
    tableFields: tableFieldsWithAction,
    showEditModal,
    paginationOptions,
    updateShopCertificateFields,
    getRow,
    handleSearch,
    handleClearClick,
    handleEditColumnsClick,
    handlePageSize,
    handlePagination,
    handleSort,
    handleBulkUploadClick,
    handleBulkUploadClose,
    handleBulkDownload,
  };
};

export default useShopCertificate;
